/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form-main');
      this.optionSelectors = this.querySelectorAll('.option-selector');
      this.data = this.getProductData();
      this.variant = this.getSelectedVariant();
      this.selectedOptions = this.getSelectedOptions();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      // Combined Listings support
      this.sectionId = this.dataset.sectionId;
      this.productId = this.dataset.productId;
      this.currentProductUrl = this.dataset.url;
      this.isCombinedListing = this.checkIfCombinedListing();

      this.addEventListener('change', this.handleVariantChange.bind(this));

      // Defer these to allow for quick-add modal to full initialize
      setTimeout(() => {
        if (!this.isCombinedListing) {
          this.updateAvailability();
        }
        this.updateAddToCartButton();
      });
    }

    /**
     * Check if this is a Combined Listing product
     */
    checkIfCombinedListing() {
      const hasProductUrls = this.querySelectorAll('.js-option[data-product-url]').length > 0;
      return hasProductUrls;
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      const optionInput = evt.target;
      const productUrl = optionInput.dataset.productUrl;
      
      // Combined Listings: navigate to different product if needed
      if (productUrl && productUrl !== this.currentProductUrl) {
        this.navigateToSiblingProduct(productUrl);
        return;
      }

      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt);
        this.updateVariantInput();
      }

      this.fireHookEvent();
      this.updateAddToCartButton();
      
      // Use different availability update for Combined Listings
      if (this.isCombinedListing) {
        this.updateCombinedListingAvailability();
      } else {
        this.updateAvailability();
      }
      
      this.updatePrice();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateMetafieldVisibility();
      VariantPicker.updateLabelText(evt);

      if (!this.preSelection) {
        this.dispatchEvent(new CustomEvent('on:variant:change', {
          bubbles: true,
          detail: {
            form: this.productForm,
            variant: this.variant,
            product: this.data.product,
            variantAvailable: this.variant
          }
        }));
      }
    }

    /**
     * Navigate to sibling product in Combined Listings
     */
    navigateToSiblingProduct(productUrl) {
      this.classList.add('loading');
      window.location.href = productUrl;
    }

    /**
     * Combined Listings availability update - keep all options enabled
     */
    updateCombinedListingAvailability() {
      this.querySelectorAll('.js-option').forEach((optionEl) => {
        VariantPicker.updateOptionAvailability(optionEl, true, true);
      });
    }

    fireHookEvent() {
        const variant = this.variant;

        // Hook for product variant change event
        this.dispatchEvent(
          new CustomEvent('theme:variant:change', {
            detail: {
              variant: variant,
            },
            bubbles: true,
          })
        );
      }

    /**
     * Shows/hides variant metafield elements on the page as necessary
     */
    updateMetafieldVisibility() {
      document.querySelectorAll('[data-variant-metafield]').forEach((elem) => {
        elem.classList.toggle('hidden', elem.dataset.variantMetafield !== this.variant?.id.toString());
      });
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      this.productForm = this.section.querySelector('.js-product-form-main');
      if (!this.productForm) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      
      // Enhanced handling for Combined Listings
      if (this.isCombinedListing) {
        const hasSelection = this.selectedOptions.some(option => option !== null);
        
        if (hasSelection) {
          this.addBtn.disabled = false;
          this.addBtn.textContent = this.addBtn.dataset.addToCartText || 'Add to Cart';
        } else {
          this.addBtn.disabled = true;
          this.addBtn.textContent = theme.strings.noSelectedVariant || 'Please select options';
        }
        return;
      }

      // Original logic for regular products
      const variantAvailable = this.variant && this.variant.available;
      this.addBtn.disabled = !variantAvailable || this.preSelection;

      if (this.preSelection) {
        this.addBtn.textContent = theme.strings.noSelectedVariant;
      } else {
        const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;
        this.addBtn.textContent = variantAvailable
          ? this.addBtn.dataset.addToCartText
          : unavailableStr;
      }
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        const label = el.nextElementSibling;
        if (label) {
          label.title = unavailableText;
        }
      } else {
        const label = el.nextElementSibling;
        if (label) {
          label.removeAttribute('title');
        }
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;
      const { availabilityMethod } = this.dataset; // 'downward' or 'selection'
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.selectedOptions };
      }

      if (availabilityMethod === 'selection') {
        // Flag all options as unavailable
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.product.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                VariantPicker.updateOptionAvailability(optionEl, true, variant.available);
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.product.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === this.selectedOptions[i] || this.selectedOptions[i] === null) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
          });
        });
      }
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const formattedData = this.data.formatted[this.variant.id];
        if (formattedData) {
          const { inventory } = formattedData;

          if (this.variant.inventory_management && inventory === 'none') {
            const backorderProdEl = this.backorder.querySelector('.backorder__product');
            const prodTitleEl = this.section.querySelector('.product-title');
            const variantTitle = this.variant.title.includes('Default')
              ? ''
              : ` - ${this.variant.title}`;

            if (backorderProdEl && prodTitleEl) {
              backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
              hideBackorder = false;
            }
          }
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      const valueEl = evt.target.nextElementSibling?.querySelector('.js-value');
      if (valueEl) {
        colorText.textContent = valueEl.textContent;
      }
    }

    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant || !this.variant.featured_media) return;

      if (this.section.matches('quick-add-drawer')) {
        this.section.updateMedia(this.variant.featured_media.id);
      } else {
        this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
        if (!this.mediaGallery) return;

        const variantMedia = this.mediaGallery.querySelector(
          `[data-media-id="${this.variant.featured_media.id}"]`
        );
        if (variantMedia) {
          this.mediaGallery.setActiveMedia(variantMedia, true, true);
        }
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-info__price > .price');
      if (!this.price) return;

      let { variant } = this;
      if (this.preSelection) {
        variant = this.data.product.variants[0];
        for (let i = 1; i < this.data.product.variants.length; i += 1) {
          if (this.data.product.variants[i].price < variant.price) variant = this.data.product.variants[i];
        }
      }

      if (this.variant) {
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');

        // Update current price and original price if on sale.
        if (this.variant.price === 0){
          if (priceCurrentEl) {
            priceCurrentEl.innerHTML = `Call for Pricing`;
          }
        } else {
          const formattedData = this.data.formatted[this.variant.id];
          if (formattedData) {
            if (priceCurrentEl) {
              priceCurrentEl.innerHTML = formattedData.price;
            }
            
            if (priceWasEl) {
              priceWasEl.innerHTML = formattedData.compareAtPrice || '';
            }

            // Update unit price, if specified.
            if (this.variant.unit_price_measurement && unitPriceEl) {
              const valueEl = this.price.querySelector('.unit-price__price');
              const unitEl = this.price.querySelector('.unit-price__unit');
              const value = this.variant.unit_price_measurement.reference_value;
              const unit = this.variant.unit_price_measurement.reference_unit;

              if (valueEl) {
                valueEl.innerHTML = formattedData.unitPrice;
              }
              if (unitEl) {
                unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
              }
            }
          }
        }

        if (unitPriceEl) {
          unitPriceEl.hidden = !this.variant.unit_price_measurement;
        }
        
        this.price.classList.toggle(
          'price--on-sale',
          this.variant.compare_at_price > this.variant.price
        );
        this.price.classList.toggle('price--sold-out', !this.variant.available && !this.preSelection);
      }
      
      const defaultEl = this.price.querySelector('.price__default');
      const noVariantEl = this.price.querySelector('.price__no-variant');
      const fromEl = this.price.querySelector('.price__from');
      
      if (defaultEl) {
        defaultEl.hidden = !this.variant && !this.preSelection;
      }
      if (noVariantEl) {
        noVariantEl.hidden = this.variant || this.preSelection;
      }
      if (fromEl) {
        fromEl.hidden = !this.preSelection;
      }
    }

    /**
     * Updates the weight.
     */
    updateWeight() {
      this.weights = this.weights || this.section.querySelectorAll('.product-info__weight');
      if (this.weights.length === 0) return;

      const weightAvailable = this.variant && this.variant.weight > 0;
      const formattedData = this.data.formatted[this.variant.id];
      
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable && formattedData ? formattedData.weight : '';
        weight.hidden = !weightAvailable;
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcodes = this.barcodes || this.section.querySelectorAll('.product-info__barcode-value');
      if (this.barcodes.length === 0) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
        if (barcode.parentNode) {
          barcode.parentNode.hidden = !barcodeAvailable;
        }
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      if (this.sku.parentNode) {
        this.sku.parentNode.hidden = !skuAvailable;
      }
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      if (this.variant) {
        window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
      }
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms =
        this.forms || this.section.querySelectorAll('.js-product-form-main, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        if (input && this.variant) {
          input.value = this.variant.id;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selectedText = selector.querySelector('.custom-select__btn')?.textContent.trim();
          selectedOptions.push(selectedText === this.dataset.placeholderText ? null : selectedText);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return dataEl ? JSON.parse(dataEl.textContent) : { product: { variants: [] }, formatted: {} };
    }

    /**
     * Get selected variant data with Combined Listings support.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();
      
      // Enhanced for Combined Listings: check for variant ID in data attributes
      if (this.isCombinedListing) {
        const checkedInputs = this.querySelectorAll('.js-option:checked');
        if (checkedInputs.length > 0) {
          const lastCheckedInput = checkedInputs[checkedInputs.length - 1];
          const variantId = lastCheckedInput.dataset.variant;
          if (variantId) {
            const variant = this.data.product.variants.find(v => v.id.toString() === variantId);
            if (variant && variant.options.every((val, index) => val === selectedOptions[index])) {
              return variant;
            }
          }
        }
      }

      return this.data.product.variants.find(
        (v) => v.options.every((val, index) => val === selectedOptions[index])
      );
    }
  }

  customElements.define('variant-picker', VariantPicker);
}